import React from 'react';
import styles from './FormField.module.css';

export interface FormFieldProps {
  /**
   * Field label text
   */
  label: string;

  /**
   * Unique ID for the input element (required for accessibility)
   */
  htmlFor: string;

  /**
   * Form input element (input, select, textarea)
   */
  children: React.ReactNode;

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Helper text to display below the input
   */
  helperText?: string;

  /**
   * Mark field as required
   * @default false
   */
  required?: boolean;

  /**
   * Additional CSS class for the field wrapper
   */
  className?: string;
}

/**
 * FormField Component
 *
 * Standardized form field wrapper with label, input, error, and helper text.
 * Follows Bauhaus design system typography and spacing.
 *
 * Features:
 * - Consistent label styling (uppercase, wide tracking)
 * - Required indicator (asterisk)
 * - Error message display with proper semantic color
 * - Helper text for additional guidance
 * - Proper spacing and alignment
 *
 * @example
 * ```tsx
 * <FormField
 *   label="Application Name"
 *   htmlFor="app-name"
 *   required
 *   error={errors.appName}
 *   helperText="Choose a unique name for your application"
 * >
 *   <input
 *     id="app-name"
 *     type="text"
 *     value={appName}
 *     onChange={(e) => setAppName(e.target.value)}
 *   />
 * </FormField>
 * ```
 */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  htmlFor,
  children,
  error,
  helperText,
  required = false,
  className,
}) => {
  const hasError = Boolean(error);

  return (
    <div className={`${styles.formField} ${className || ''}`}>
      <label htmlFor={htmlFor} className={styles.label}>
        {label}
        {required && <span className={styles.required} aria-label="required"> *</span>}
      </label>
      <div className={`${styles.inputWrapper} ${hasError ? styles.hasError : ''}`}>
        {children}
      </div>
      {error && (
        <div className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}
      {helperText && !error && (
        <div className={styles.helperText}>
          {helperText}
        </div>
      )}
    </div>
  );
};
